import { SupabaseClient } from '@supabase/supabase-js';

import {
  RenderImageError,
  RenderImageErrorCode,
} from 'modules/manager/domain/render/errors/render-image-errors';

export const RENDERED_IMAGES_BUCKET = 'images-media-manager';
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = 'image/png';
const PUBLIC_URL_RE = /^.*\/storage\/v1\/object\/public\/images-media-manager\/(.+)$/;

export class RenderedImagesStorage {
  constructor(private readonly supabase: SupabaseClient) {}

  buildPath(matchId: number, boardId: number): string {
    return `matches/${matchId}/${boardId}-${randomUuid()}.png`;
  }

  async upload(
    matchId: number,
    boardId: number,
    dataUrl: string,
  ): Promise<{ path: string; publicUrl: string }> {
    const blob = decodeDataUrl(dataUrl);
    const path = this.buildPath(matchId, boardId);

    const { error: uploadError } = await this.supabase.storage
      .from(RENDERED_IMAGES_BUCKET)
      .upload(path, blob, { upsert: true, contentType: ALLOWED_MIME });

    if (uploadError) {
      throw classifyStorageError(uploadError, 'upload', { matchId, boardId, path });
    }

    const { data } = this.supabase.storage.from(RENDERED_IMAGES_BUCKET).getPublicUrl(path);
    if (!data?.publicUrl) {
      throw new RenderImageError(
        RenderImageErrorCode.INVALID_URL,
        `[supabase] getPublicUrl returned empty url for path=${path}`,
        'Image was saved but its address could not be generated. Please try again.',
      );
    }

    return { path, publicUrl: data.publicUrl };
  }

  async remove(publicUrl: string): Promise<void> {
    const path = extractObjectPath(publicUrl);
    if (!path) {
      throw new RenderImageError(
        RenderImageErrorCode.INVALID_URL,
        `[supabase] could not extract object path from public url: ${publicUrl}`,
        'The image address is malformed and the file could not be removed.',
      );
    }
    const { error } = await this.supabase.storage.from(RENDERED_IMAGES_BUCKET).remove([path]);
    if (error) {
      throw classifyStorageError(error, 'remove', { path });
    }
  }
}

const randomUuid = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const decodeDataUrl = (dataUrl: string): Blob => {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match || match[1] !== ALLOWED_MIME) {
    throw new RenderImageError(
      RenderImageErrorCode.INVALID_PAYLOAD,
      `[render] expected ${ALLOWED_MIME} data URL, got "${(match?.[1] ?? '').slice(0, 40)}"`,
      'The generated image is not a valid PNG.',
    );
  }
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  if (bytes.byteLength > MAX_BYTES) {
    throw new RenderImageError(
      RenderImageErrorCode.INVALID_PAYLOAD,
      `[render] image exceeds size limit: ${bytes.byteLength} > ${MAX_BYTES}`,
      'The generated image is too large to upload.',
    );
  }
  return new Blob([bytes], { type: ALLOWED_MIME });
};

const extractObjectPath = (publicUrl: string): string | null => {
  const m = PUBLIC_URL_RE.exec(publicUrl);
  return m ? m[1] : null;
};

const classifyStorageError = (
  err: { message?: string; statusCode?: string; status?: number } & object,
  op: 'upload' | 'remove',
  ctx: Record<string, unknown>,
): RenderImageError => {
  const message = err.message ?? '';
  const ctxStr = Object.entries(ctx)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(' ');

  if (err.statusCode === '403' || err.status === 403) {
    return new RenderImageError(
      RenderImageErrorCode.PERMISSION_DENIED,
      `[supabase] storage ${op} permission denied (${ctxStr}): ${message}`,
      'You do not have permission to save this image.',
      err,
    );
  }
  if (/bucket/i.test(message)) {
    return new RenderImageError(
      RenderImageErrorCode.BUCKET_FAILED,
      `[supabase] bucket-level failure during ${op} (${ctxStr}): ${message}`,
      'The image storage is unavailable. Please try again later.',
      err,
    );
  }
  if (op === 'remove' && /not.?found|no such/i.test(message)) {
    return new RenderImageError(
      RenderImageErrorCode.MISSING_FILE,
      `[supabase] file missing during remove (${ctxStr}): ${message}`,
      'The image file was already removed.',
      err,
    );
  }
  if (op === 'remove') {
    return new RenderImageError(
      RenderImageErrorCode.DELETION_FAILED,
      `[supabase] storage remove failed (${ctxStr}): ${message}`,
      'Failed to remove the image. Please try again.',
      err,
    );
  }
  return new RenderImageError(
    RenderImageErrorCode.UPLOAD_FAILED,
    `[supabase] storage upload failed (${ctxStr}): ${message}`,
    'Failed to save the image. Please try again.',
    err,
  );
};
